<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { getNeed } from '@/api/upload.js'
import { message } from 'ant-design-vue'
import { getProportion, getDataSource, drawRect } from '@/utils/coord.js'
import { useRoute } from 'vue-router'
import RightDec from '@/views/recognition_general/right-dec.vue'
import Application from '@/views/recognition_general/application1.vue'
import Tips from '../recognition_general/tips.vue'
import { useRecognitionImages } from '@/utils/useRecognitionImages'
import { resolveApiAssetUrl } from '@/utils/algorithmPublicAssets'

const { loadRecognitionImages, resetRecognitionImages } = useRecognitionImages()

const show = ref(false)
const visible = ref(false)
const value = ref('')
const loading = ref(false)
const activeImage = ref(null)
const isNeedVerify = ref(false)
//路由
import { getRouteName } from '@/views/algorithm-detail/utils.js'

const route = useRoute()
const routeName = getRouteName(route)
const detailData = inject('detailData', null)
const routeSource = computed(() => detailData.value?.algorithm_type?.type || '')
const exampleImages = computed(() =>
  (detailData.value?.demo?.example_images || []).map(resolveApiAssetUrl),
)
const exampleCut = computed(() => {
  const cut = detailData.value?.demo?.example_cut || {}
  const result = {}
  Object.keys(cut).forEach((key) => {
    result[key] = (cut[key] || []).map((item) => ({
      ...item,
      url: resolveApiAssetUrl(item.url),
    }))
  })
  return result
})

const curData = computed(() => detailData.value || {})

const empty = ref(false)
const canvasW = ref(0)
const canvasH = ref(0)
const canvasRef = ref(null)
const originalImg = ref('')
const originalShow = ref(false)
const result = ref({})

const fileInput = ref(null)
// 右侧小图选中
const active = ref(-2)
const selectItem = ref({})
//右侧小图列表
const cropList = ref([])
const callable = ref(false)

const OcrType = [
  'ocrBusinessLicense',
  'ocrBankCard',
  'ocrInvoiceInformation',
  'ocrDriverLicense',
  'ocrHandwritten',
  'classificationAnimal',
  'classificationVegetables',
  'classificationVehicleType',
  'classificationDishes',
  'classificationLandmark',
  'imageTechGeneration',
  'classificationGeneral',
  'faceAppearanceLevel',
].includes(routeName)

const source = ref('face')
onMounted(async () => {
  activeImage.value = exampleImages.value[0] || null
  handleActive(activeImage.value, 0)
  source.value = routeSource.value.split('_')[0]
})

const handleActive = (item, idx) => {
  resetRecognitionImages()
  activeImage.value = item
  result.value = {}
  selectItem.value = {}
  if (item) empty.value = false
  originalShow.value = false
  const cut = exampleCut.value[idx + 1]
  if (cut) {
    cropList.value = cut
    if (OcrType) {
      selectItem.value = cut[0]
    }
    // 如果 cropList 有数据，默认选中（有label选第二项，否则选第一项）
    if (cut.length > 1 && cut[1].label !== undefined) {
      active.value = 1
      selectItem.value = cut[1]
    } else if (cut.length > 0) {
      active.value = 0
      selectItem.value = cut[0]
    }
  }
  const image = document.getElementById('previewImg')
  const ctx = canvasRef.value.getContext('2d')
  image.onload = function (event) {
    const size = getProportion(
      event.target.naturalWidth,
      event.target.naturalHeight,
      585,
      370,
      585,
      370,
    )

    canvasRef.value.width = size.width
    canvasRef.value.height = size.height

    ctx.drawImage(image, 0, 0, Math.ceil(size.width), Math.ceil(size.height))
    if (active.value === -1) {
      const shapes = result.value.shapes || []
      if (shapes.length) originalShow.value = true
    }
  }
}

const triggerFileInput = async () => {
  let res = await getNeed()
  isNeedVerify.value = res.need
  callable.value = res.callable
  if (!res.callable) {
    message.warning('今天的试用次数已用完，请联系咨询', 4)
    return
  }
  if (!res.need) {
    fileInput.value.click()
  } else {
    visible.value = true
    value.value = ''
  }
}

const showLeftImage = (item, id) => {
  active.value = id
  selectItem.value = item
  activeImage.value = item.url
  if (id === -1) {
    drawImage(result.value)
  } else {
    activeImage.value = item.url
  }
}

const drawImage = async (res, crop = false) => {
  const el = canvasRef.value.getContext('2d')
  el.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  const image = document.getElementById('previewImg')
  const size = getProportion(
    res.imageWidth,
    res.imageHeight,
    585,
    370,
    585,
    370,
  )
  canvasRef.value.width = size.width
  canvasRef.value.height = size.height
  image.width = size.width
  image.height = size.height
  el.drawImage(image, 0, 0, canvasRef.value.width, canvasRef.value.height)
  const shapes = res.shapes || []
  shapes.forEach((i) => {
    if (shapes.length) originalShow.value = true
    if (i.points.length) {
      const _data = getDataSource(
        i,
        res.imageWidth,
        res.imageHeight,
        canvasRef.value.width,
        canvasRef.value.height,
      )
      drawRect(el, {
        ...i,
        points: _data,
      })
    }
  })
  if (crop) {
    const images = await loadRecognitionImages(shapes, route.query.id || detailData.value?.id)
    if (!images) return
    cropList.value = images
  }
  //营业执照显示文字
  if (OcrType) {
    selectItem.value = cropList.value[0]
  }
}
</script>

<template>
  <div class="section common-algorithm-experience">
    <div class="content">
      <h2 class="title">功能体验</h2>
      <div class="content">
        <div class="image-preview">
          <div class="preview-left">
            <div class="global-mask-over">
              <div :class="['global-custom-area', show && 'show']"></div>
            </div>
            <div class="preview-img">
              <div v-if="loading" class="loading"><a-spin size="large" /></div>
              <img id="previewImg" :src="activeImage" class="previewImg" />
              <canvas
                id="canvas"
                ref="canvasRef"
                class="canvas"
                :width="canvasW"
                :height="canvasH"
              />
            </div>
            <div class="slick-thumb">
              <li
                v-for="(item, idx) in exampleImages"
                :key="idx"
                @click="handleActive(item, idx)"
                :class="[activeImage === item && 'active']"
              >
                <img :src="item" />
              </li>
            </div>
          </div>
          <div class="preview-right">
            <div class="right-title">分析结果</div>
            <div class="slide-container" v-if="!OcrType">
              <div
                :class="['img-border', { 'img-border-active': active === -1 }]"
                v-if="originalShow"
              >
                <img
                  class="img"
                  :src="originalImg"
                  @click="
                    showLeftImage(
                      {
                        url: originalImg,
                      },
                      -1,
                    )
                  "
                />
              </div>
              <div
                :class="['img-border', { 'img-border-active': active === idx }]"
                v-for="(i, idx) in cropList"
              >
                <img class="img" :src="i.url" @click="showLeftImage(i, idx)" />
              </div>
            </div>
            <div class="right-dec">
              <p v-show="selectItem.label !== undefined">
                <RightDec
                  :selectItem="selectItem"
                  :routeName="routeName"
                  :curData="curData"
                />
              </p>
            </div>
          </div>
        </div>
      </div>

      <Tips :source="source" :canUpload="false" />
    </div>
  </div>

  <Indicator />
  <IntelligentModule />

  <div v-if="curData.application">
    <Application :list="curData.application" />
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/styles/main.scss';
li {
  list-style: none;
}
.mb10 {
  margin-bottom: 0;
  height: 27px;
}
.ml0 {
  margin-left: 0 !important;
  margin-right: 5px;
}
.flex {
  display: flex;
}

.section {
  background-color: #fff;
  padding: 96px 0;

  .content {
    width: 1181px;
    margin: auto;

    .title {
      font-size: 30px;
      font-weight: 500;
      color: #000000;
      text-align: center;
      margin-bottom: 70px;
    }

    .image-preview {
      margin-top: 50px;
      display: flex;
      justify-content: space-between;
      align-items: stretch;

      .preview-left {
        background: #ffffff;
        display: flex;
        flex-direction: column;
        width: 585px;
        position: relative;
        margin-right: 30px;
        .upload-image {
          width: 136px;
          height: 86px;
          border-radius: 3px;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          color: #707078;
          cursor: pointer;
          background: #220079;
          color: #fff;
          font-size: 12px;
          z-index: 100;
          .image {
            font-size: 20px;
          }
          .center {
            margin-top: 5px;
          }
        }

        .global-mask-over {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          height: 370px;
          .global-custom-area {
            position: absolute;
            bottom: -66px;
            width: 100%;
            z-index: 600;
            height: 64px;
            line-height: 64px;
            background-color: rgba(0, 0, 0, 0.3);
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.27);
            transition: all 0.3s ease;
          }

          .show {
            bottom: 0px !important;
          }
        }
      }

      .preview-right {
        background: #f7f7f7;
        width: 566px;
        position: relative;
        padding: 26px 43px 26px 45px;

        .right-title {
          font-weight: 500;
          font-size: 30px;
          color: #000;
          text-align: left;
          margin-bottom: 30px;
        }

        .slide-container {
          border-radius: 4px;
          min-height: 97px;
          border-radius: 4px;
          display: flex;
          overflow-x: auto;

          .img-border {
            width: auto;
            height: 89px;
            margin-right: 8px;
            cursor: pointer;
            border: 2px solid transparent;
            box-sizing: border-box;
            position: relative;
            img {
              width: auto;
              height: 85px;
              object-fit: fill;
            }
            &:last-child {
              margin-right: 0;
            }
          }

          .img-border-active {
            border-color: #ed000c;
            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(237, 0, 12, 0.15);
              pointer-events: none;
            }
          }
        }
      }

      .preview-img {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 585px;
        height: 370px;
        position: relative;
        background: #f7f7f7;

        img {
          width: 100%;
          max-height: 100%;
          position: relative;
        }

        .canvas,
        .empty {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .loading {
          width: 585px;
          height: 370px;
          background-color: rgba(0, 0, 0, 0.2);
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 500;
        }
      }
    }

    .previewImg {
      display: none;
    }
  }

  .tips {
    margin-top: 24px;
    color: #666;
  }

  .tips2 {
    font-size: 12px;
  }

  .bottom-tip {
    position: absolute;
    bottom: 26px;
    text-align: left;
    color: #666666;
  }
}

.row {
  padding: 15px 20px;
  .verify-img {
    width: 75px;
    height: 30px;
    margin-left: -4px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 0 6px 6px 0;
    position: relative;
    z-index: 1001;
  }
}

.slick-thumb {
  margin-top: 40px;
  display: flex;
  li {
    width: 136px;
    height: 86px;
    cursor: pointer;
    margin-right: 13px;

    img {
      width: 100%;
      height: 100%;
      filter: grayscale(100%);
      display: block;
    }
  }

  .active {
    position: relative;
    box-shadow: 0 0 0 2px #5e87e8;
  }
}

.right-dec {
  margin-top: 10px;
}

.empty-item {
  color: #666;
  font-size: 16px;
  p {
    text-wrap: nowrap;
  }
}

.attribute {
  min-width: 140px;
  display: inline-block;
}

:deep() {
  .slick-dots {
    position: relative;
    height: auto;
  }

  .slick-slide img {
    border: 5px solid #fff;
    display: block;
    margin: auto;
    max-width: 80%;
  }

  slick-thumb li.slick-active img {
    filter: grayscale(0%);
  }

  .slick-thumb li img {
    filter: grayscale(0%);
  }

  .row-item {
    display: flex;
    align-items: center;
    .item {
      white-space: nowrap;
    }

    .vague {
      font-size: 12px;
      color: #ff0000;
      margin-left: 12px;
      cursor: pointer;
    }
  }

  .row-tips {
    height: 20px;
    font-size: 10px;
    line-height: 1.7;
    text-align: left;
    color: #999999;
    width: 100%;
    padding-left: 57px;

    .error {
      color: #ff4d4f;
    }
  }

  .ant-popover .ant-popover-inner {
    padding: 30px !important;
  }

  .ant-btn-primary {
    background: #220079;
    &:hover {
      background: #220079;
    }
  }
}
</style>
